import type { AddOnQuantity } from "@circle-react/types";

import type { PlanTiers } from "@/react/helpers/planHelpers";
import type { Plan } from "@/react/types/Plan";

interface PlanAddonInfo {
  monthly_amount: string;
  value: AddOnQuantity;
}

interface PlanAddonInfoHash {
  [key: string]: PlanAddonInfo;
}

const selectPlan = (planType: PlanTiers) => (plan: Plan) =>
  plan.tier === planType;

const selectPlanAddonProperty =
  (key: string, property: keyof PlanAddonInfo) => (plan: Plan) => {
    if (!Array.isArray(plan?.plan_addons_info)) {
      return undefined;
    }

    const featureHash = plan.plan_addons_info.find(hash =>
      Object.hasOwn(hash, key),
    ) as PlanAddonInfoHash | undefined;

    if (featureHash && featureHash[key]) {
      return featureHash[key][property];
    }
    return undefined;
  };

const selectFeature = (key: string) => selectPlanAddonProperty(key, "value");
const selectMonthlyPrice = (key: string) =>
  selectPlanAddonProperty(key, "monthly_amount");

export const getPlanAddonValue = (
  plans: Plan[],
  planType: PlanTiers,
  key: string,
): AddOnQuantity | string => {
  const plan = plans.find(selectPlan(planType));

  if (!plan) {
    return "";
  }

  const featureValue = selectFeature(key)(plan);
  return featureValue ?? "";
};

export const getPlanAddonPrice = (
  plans: Plan[],
  planType: PlanTiers,
  key: string,
): AddOnQuantity | string => {
  const plan = plans.find(selectPlan(planType));

  if (!plan) {
    return "";
  }

  const monthlyPrice = selectMonthlyPrice(key)(plan);

  return monthlyPrice ?? "";
};
